import { create } from 'zustand';
import { getState, useState } from '@/framework';
import { IExportState, ILayerProps, StateType } from '@/version-types';
import { DefaultEliminatePenSize } from '../constants';
import { EliminateConfig, EliminateType, PenType } from '../types';

/** 状态 */
export interface IState {
    /** 消除笔完整配置 */
    eliminateConfig: EliminateConfig;
    /** 原始图层信息 */
    originLayer: ILayerProps | null;
}

/** 动作 */
export interface IAction {
    setEliminateConfig: (config: Partial<EliminateConfig>) => void;
    setOriginLayer: (layer: ILayerProps | null) => void;
    destroy: () => void;
}

/** 存储类型 */
export interface IStore extends IState, IAction {}

/** 初始化状态 */
function getInitialState(): IState {
    return {
        originLayer: null,
        eliminateConfig: {
            type: EliminateType.AI,
            penSize: DefaultEliminatePenSize,
            penType: PenType.BRUSH,
            // ATTENTION: AI 消除模式下，默认开启手动应用消除
            manualApply: true,
            canApply: false,
            selectionType: 'add',
            autoRecognizeWatermark: false,
            autoRecognizeText: false,
            autoRecognizeGlasses: false,
            autoRecognizePasserby: false,
            autoGlare: false,
            autoRecognizeLogo: false,
            watermarkMaskURL: '',
            textMaskURL: '',
            isWatermarkMaskUsed: false,
            isTextMaskUsed: false,
            isPasserbyMaskUsed: false,
            isGlassesMaskUsed: false,
            recognizeInfo: {},
        },
    };
}

/** 创建存储 */
const createState = (identity: string) => {
    const store = create<IStore>((set, get) => ({
        ...getInitialState(),
        setEliminateConfig(config) {
            set((state) => {
                const eliminateConfig = {
                    ...state.eliminateConfig,
                    ...config,
                };

                return {
                    eliminateConfig: {
                        ...eliminateConfig,
                        // ATTENTION: AI 消除模式下，默认开启手动应用消除
                        manualApply: eliminateConfig.type === EliminateType.AI,
                    },
                };
            });
        },
        setOriginLayer(layer) {
            set({ originLayer: layer });
        },
        destroy() {
            set(getInitialState());
        },
    }));

    return store;
};

/** 存储名称 */
const name = 'eliminate-pen-state';

/** 存储类型 */
type ModuleType = StateType<IStore, typeof name>;

/** hook 获取存储 */
export const useEliminatePenState = () => {
    return useState<ModuleType>()[name];
};

/** 通过 id 获取存储 */
export const getEliminatePenState = (identity: string) => {
    return getState<ModuleType>(identity)[name].getState();
};

export const watchEliminatePenState = (identity: string, callback: (state: IStore) => void) => {
    return getState<ModuleType>(identity)[name].subscribe((state, preState) => {
        if (JSON.stringify(preState) !== JSON.stringify(state)) {
            callback(state);
        }
    });
};

/** 侦听消除笔配置变化 */
export const watchEliminatePenConfig = (identity: string, callback: (config: EliminateConfig) => void) => {
    return getState<ModuleType>(identity)[name].subscribe((state, preState) => {
        if (JSON.stringify(preState.eliminateConfig) !== JSON.stringify(state.eliminateConfig)) {
            callback(state.eliminateConfig);
        }
    });
};

const exportState: IExportState = {
    name,
    createState,
};

export default exportState;
